import { NzI18nService } from 'ng-zorro-antd/i18n';
import { FALLBACK_LANGUAGE, LANGUAGES_MAP } from '../constants/i18n.constants';
import { LanguageCode, LanguageConfig } from '../models/i18n.models';
import { i18nInit } from '@localization';
import { inject } from '@angular/core';

/**
 * Initializes the localization process by injecting the necessary dependencies
 * and setting up the internationalization flow. It binds a callback function
 * to handle language changes and sets the locale and date locale accordingly.
 *
 * @returns A function to initialize the i18n flow.
 */
export const localizeInit = () => {
  // Inject the NzI18nService to handle locale and date locale settings
  const nzI18n = inject(NzI18nService);

  // Call i18nInit with a callback to set the language when it changes
  return i18nInit((lang: string) => setLanguage(lang, nzI18n));
};

/**
 * Sets the locale and date locale based on the selected language.
 * It retrieves the corresponding configuration from LANGUAGES_MAP.
 * If the specified language is not found, it falls back to fallback language.
 *
 * @param lang - The language code selected by the user.
 * @param nzI18n - The NzI18nService instance used to update localization settings.
 */
export const setLanguage = (lang: string, nzI18n: NzI18nService): void => {
  // Retrieve the language configuration; fallback to fallback language if not found
  const languageConfig: LanguageConfig = LANGUAGES_MAP[lang as LanguageCode] || LANGUAGES_MAP[FALLBACK_LANGUAGE];

  // Update the locale for UI components
  nzI18n.setLocale(languageConfig.locale);

  // Update the date locale for date-specific components
  nzI18n.setDateLocale(languageConfig.dateLocale);
};
